/**
 * voice-v8 — Teilen-Helper für Medien-Bubbles (pre.152).
 *
 * Erste Wahl: Native Web Share API (navigator.share). Fallback (nur ohne
 * diese API, also v.a. auf aelteren Desktop-Browsern): kleines Modal mit
 * WhatsApp / Telegram / Link-kopieren / Herunterladen (bei Dateien).
 * Funktioniert auf iOS (Safari hat die API), Android (Chrome), Desktop-Chrome.
 */

const SVG_NS = "http://www.w3.org/2000/svg";

/** So lange steht „Link kopiert“, bevor das Rückfall-Blatt sich schließt (Ticket 2892). */
const KOPIERT_ANZEIGE_MS = 1100;

export interface ShareInput {
  /** Absolute URL (auf relative Pfade hinweisen wir auf location.origin). */
  url: string;
  /** Optionaler Titel (wird im Share-Sheet als headline angezeigt). */
  title?: string;
  /** Optionaler Begleittext (für Chats). */
  text?: string;
  /**
   * Wie die Datei im Teilen-Blatt heissen soll.
   *
   * Ohne den Namen heisst der Klang des Tages `9b97870b-6a8c-….mp3` — und
   * so liegt er dann auch in „Dateien". Ein Name wie
   * `Klang-des-Tages-2026-09-06.mp3` ist der Unterschied zwischen einer
   * Sammlung und einem Haufen.
   */
  dateiname?: string;
  /**
   * Erzwingt den Datei-Weg (Blob holen, als Datei anhängen), auch wenn die
   * URL keine Endung trägt. (11.09.2026)
   *
   * ⚠️ Die bisherige Erkennung sah nur auf die URL: Endung wie `.mp3`, oder
   *    `/uploads/` im Pfad. Vorgelesener Text kommt aber über
   *    `/api/tts/stream?token=…` — keine Endung, kein `/uploads/`, obwohl
   *    der Server `Content-Type: audio/mpeg` liefert. Diese Zeile fiel durch
   *    die Erkennung und teilte nur die (dann nutzlose) Adresse mit dem
   *    einmaligen Token statt der Datei.
   *
   * Ein Flag statt einer wachsenden Liste von URL-Mustern: Jede neue
   * Audio-Route sonst wieder eine Regex-Ergänzung, die man vergessen kann.
   */
  alsDatei?: boolean;
  /**
   * Die Datei selbst, wenn der Aufrufer sie schon geholt hat (die
   * PDF-Ansicht, 28.09.2026). Dann entfaellt der zweite Abruf, und das
   * Teilen-Blatt geht noch im selben Tipp auf.
   */
  datei?: Blob;
}

/**
 * Was als DATEI ins Teilen-Blatt darf. Anderes (etwa eine Fehlerseite statt
 * der Datei) geht als Adresse.
 *
 * 28.09.2026 (Ticket 2396): auch PDF. Vorher ging eine Rechnung als blosse
 * Adresse hinaus — und die oeffnet beim Empfaenger nur die Anmeldung.
 */
function istTeilbareDatei(art: string): boolean {
  return art.startsWith("image/") || art.startsWith("audio/") || art === "application/pdf";
}

function absolutize(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return new URL(url, location.origin).toString();
}

/**
 * Steckt hinter dieser Adresse eine DATEI — soll `shareMedia` sie holen und
 * anhängen, statt nur die Adresse zu teilen? (11.09.2026)
 *
 * Als eigene, geprüfte Funktion herausgezogen: Der Vorlese-Knopf teilte
 * Adressen wie `/api/tts/stream?token=…` — keine Endung, kein `/uploads/`,
 * also fiel er bisher durch jede Erkennung, obwohl der Server dahinter eine
 * echte MP3-Datei ausliefert. `erzwingen` ist der Fluchtweg für Aufrufer,
 * die es besser wissen als die URL-Form.
 */
export function istDateiUrl(url: string, erzwingen?: boolean): boolean {
  return (
    erzwingen === true ||
    /\.(png|jpe?g|webp|gif|heic|mp3|m4a|wav|aac|ogg)(\?|$)/i.test(url) ||
    url.includes("/uploads/")
  );
}

interface NavigatorWithShare {
  share?: (data: ShareData) => Promise<void>;
  canShare?: (data: ShareData) => boolean;
}

export async function shareMedia(input: ShareInput): Promise<void> {
  const url = absolutize(input.url);
  const data: ShareData = {
    url,
    ...(input.title ? { title: input.title } : {}),
    ...(input.text ? { text: input.text } : {}),
  };

  const nav = navigator as Navigator & NavigatorWithShare;

  // Pre.793 (#67): Bilder als DATEI teilen — nur-URL öffnete z.B. WhatsApp
  // ohne Anhang (Martins generiertes Bild kam nie an). Bild holen, als File
  // anhängen wenn das Gerät es kann; scheitert irgendwas → URL-Share wie
  // bisher (kein Verhalten verloren).
  /*
   * ⚠️ 07.09.2026 — auch TON wird als Datei geteilt, nicht nur als Adresse.
   *
   * Martin, zum Klang des Tages: *„Ich wuerde gerne den Player erweitern mit
   * einem Download-Button … damit ich die Lieder auch zusaetzlich speichern
   * und abspielen kann."*
   *
   * Auf dem iPhone gibt es keinen Download-Ordner; `<a download>` tut in der
   * WKWebView nichts. Der Weg, der dort wirklich speichert, ist das
   * Teilen-Blatt mit einer DATEI daran — „In Dateien sichern" ist der
   * Download. Genau dieselbe Mechanik, die Bilder seit pre.793 nutzen; sie
   * bekommt hier nur einen zweiten Dateityp.
   */
  const alsDatei = istDateiUrl(url, input.alsDatei);
  if (nav.share && nav.canShare && (alsDatei || input.datei)) {
    try {
      const blob =
        input.datei ??
        (await fetch(url, { credentials: "include" }).then((res) => (res.ok ? res.blob() : null)));
      if (blob && istTeilbareDatei(blob.type)) {
        const name =
          input.dateiname ||
          url.split("/").pop()?.split("?")[0] ||
          (blob.type.startsWith("audio/") ? "klang.mp3" : "bild.png");
        const file = new File([blob], name, { type: blob.type });
        const fileData: ShareData = {
          files: [file],
          ...(input.title ? { title: input.title } : {}),
          ...(input.text ? { text: input.text } : {}),
        };
        if (nav.canShare(fileData)) {
          await nav.share(fileData);
          return;
        }
      }
    } catch (err) {
      if ((err as DOMException).name === "AbortError") return;
      // weiter mit URL-Share
    }
  }

  if (nav.share) {
    try {
      await nav.share(data);
      return;
    } catch (err) {
      // User-Cancel ist OK; alles andere → Fallback.
      if ((err as DOMException).name === "AbortError") return;
    }
  }
  openShareFallback(url, input.text || input.title || "", alsDatei ? input.dateiname : undefined);
}

/** SVG-Icon für den Teilen-Button. */
export function makeShareIcon(size = 16): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("fill", "currentColor");
  path.setAttribute(
    "d",
    "M18 16.08a3 3 0 0 0-2.4 1.2L8.91 14.4a3.36 3.36 0 0 0 0-1.46l6.61-3.85a3 3 0 1 0-1-2.27 3.4 3.4 0 0 0 0 .54L7.91 10.92a3 3 0 1 0 0 2.16l6.62 3.86a3 3 0 0 0-.06.55 2.94 2.94 0 1 0 2.94-2.94z",
  );
  svg.appendChild(path);
  return svg;
}

/**
 * @param dateiname Gesetzt, wenn eine Datei dahintersteckt (Bild/Ton) — dann
 *   bekommt das Blatt einen echten „Herunterladen"-Punkt. Ohne native Share-
 *   API (dieser Weg wird NUR dann betreten) ist `<a download>` das richtige
 *   Werkzeug: Die WKWebView-Einschränkung, die den Datei-Weg oben nötig
 *   macht, betrifft genau die Geräte, die `navigator.share` mitbringen —
 *   wer hier landet, hat einen gewöhnlichen Browser, auf dem der Link
 *   funktioniert. (11.09.2026, Martin: *„ich bin nicht einmal sicher, ob das
 *   Herunterladen funktioniert."* — auf diesem Weg stimmte das: Es gab
 *   keinen Download-Punkt, nur WhatsApp/Telegram/Link-kopieren.)
 */
function openShareFallback(url: string, text: string, dateiname?: string): void {
  const overlay = document.createElement("div");
  overlay.className = "share-overlay";
  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay) overlay.remove();
  });

  const sheet = document.createElement("div");
  sheet.className = "share-sheet";
  // Ticket 2892: Rückmeldungen („Link kopiert“) werden vorgelesen, ohne den Fokus zu verschieben.
  sheet.setAttribute("aria-live", "polite");

  const title = document.createElement("h3");
  title.className = "share-title";
  title.textContent = "Teilen";
  sheet.appendChild(title);

  const opts: Array<{ label: string; onClick: (btn: HTMLButtonElement) => void | Promise<void>; primary?: boolean }> =
    [
      {
        label: "WhatsApp",
        onClick: () => {
          const t = encodeURIComponent(text ? `${text}\n${url}` : url);
          window.open(`https://wa.me/?text=${t}`, "_blank", "noopener");
          overlay.remove();
        },
      },
      {
        label: "Telegram",
        onClick: () => {
          const u = encodeURIComponent(url);
          const t = encodeURIComponent(text || "");
          window.open(
            `https://t.me/share/url?url=${u}&text=${t}`,
            "_blank",
            "noopener",
          );
          overlay.remove();
        },
      },
      {
        label: "Link kopieren",
        onClick: async (btn) => {
          // Ticket 2892: ruhige Rückmeldung statt stillem Schließen — der Knopf sagt, was geschah (Vorleser
          // hören es über die Live-Region des Blatts). Das Blatt schließt erst danach; scheitert das Kopieren,
          // bleibt es offen und sagt es, damit man den Link anders holen kann.
          let kopiert = false;
          try {
            await navigator.clipboard.writeText(url);
            kopiert = true;
          } catch {
            /* keine Zwischenablage (unsicherer Kontext, Berechtigung verweigert) */
          }
          btn.textContent = kopiert ? "Link kopiert" : "Kopieren nicht möglich";
          if (kopiert) window.setTimeout(() => overlay.remove(), KOPIERT_ANZEIGE_MS);
        },
      },
      // 11.09.2026: nur hier, nicht als vierte Wahl bei jedem Teilen —
      // ohne echte Datei (z.B. beim Teilen einer reinen Web-Adresse) gibt es
      // nichts zum Herunterladen.
      ...(dateiname
        ? [
            {
              label: "Herunterladen",
              onClick: (): void => {
                const a = document.createElement("a");
                a.href = url;
                a.download = dateiname;
                document.body.appendChild(a);
                a.click();
                a.remove();
                overlay.remove();
              },
            },
          ]
        : []),
      {
        label: "Abbrechen",
        onClick: () => overlay.remove(),
      },
    ];

  for (const o of opts) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "share-btn";
    btn.textContent = o.label;
    btn.addEventListener("click", () => void o.onClick(btn));
    sheet.appendChild(btn);
  }

  overlay.appendChild(sheet);
  document.body.appendChild(overlay);
}
